import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Card, CardContent, TextField,
  Button, Checkbox, FormControlLabel, CircularProgress, Alert, Stack,
  Chip, Divider, Accordion, AccordionSummary, AccordionDetails,
  InputAdornment, IconButton, Paper, Tooltip
} from '@mui/material';
import {
  Telegram, CheckCircle, WarningAmber, Security, Bolt, TrendingUp,
  NotificationsActive, ExpandMore, Speed, Lock, Star, Shield,
  PersonOutline, MailOutline, PhoneAndroid, Visibility, VisibilityOff,
  ArrowForward, Check, VerifiedUser, WorkspacePremium, AccountBalanceWallet,
  Discount
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import PublicNavbar from '../components/common/PublicNavbar';
import PublicFooter from '../components/common/PublicFooter';
import { telegramSubscription } from '../services/api';
import useStore from '../store/useStore';
import { openRazorpayCheckout } from '../utils/razorpay';

export default function LandingPage() {
  const navigate = useNavigate();
  const { setToken, setUser } = useStore();

  const [formData, setFormData] = useState({
    fullName: '',
    mobileNumber: '',
    email: '',
    password: '',
    confirmPassword: '',
    termsAccepted: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Coupon state
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponInput, setCouponInput] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  // Order and User state
  const [currentOrder, setCurrentOrder] = useState(null);
  const [registeredUser, setRegisteredUser] = useState(null);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errorMsg) setErrorMsg('');
  };

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    setCouponLoading(true);
    try {
      const res = await telegramSubscription.applyCoupon(couponInput.trim());
      if (res.data?.success) {
        setAppliedCoupon(res.data.coupon);
        toast.success(res.data.message || 'Coupon applied successfully!');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid or expired coupon code');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput('');
    toast.info('Coupon removed');
  };

  const handleRegisterAndPay = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    // Client-side validations
    if (!formData.fullName.trim()) {
      setErrorMsg('Please enter your full name.');
      return;
    }
    const cleanMobile = formData.mobileNumber.replace(/[^0-9]/g, '');
    if (cleanMobile.length !== 10) {
      setErrorMsg('Please enter a valid 10-digit mobile number.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      setErrorMsg('Please enter a valid email address.');
      return;
    }
    if (!formData.password || formData.password.length < 6) {
      setErrorMsg('Password must be at least 6 characters.');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setErrorMsg('Passwords do not match.');
      return;
    }
    if (!formData.termsAccepted) {
      setErrorMsg('Please check the box to agree to the Terms & Conditions and Privacy Policy.');
      return;
    }

    setLoading(true);

    try {
      // 1. Register or update account & get JWT token
      const regRes = await telegramSubscription.register({
        fullName: formData.fullName.trim(),
        mobileNumber: cleanMobile,
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        termsAccepted: true,
        termsVersion: '1.0',
        privacyVersion: '1.0',
      });

      if (!regRes.data?.success || !regRes.data?.token) {
        throw new Error(regRes.data?.message || 'Registration failed');
      }

      const authToken = regRes.data.token;
      setToken(authToken);
      setUser(regRes.data.user);
      setRegisteredUser(regRes.data.user);

      // 2. Create Razorpay Order with optional coupon
      const couponCode = appliedCoupon ? appliedCoupon.code : null;
      const orderRes = await telegramSubscription.createOrder({ couponCode });
      if (!orderRes.data?.success) {
        throw new Error(orderRes.data?.message || 'Unable to create payment order');
      }

      const orderData = orderRes.data;
      setCurrentOrder(orderData);

      // Handle 100% free coupon order
      if (orderData.isFree) {
        const verifyRes = await telegramSubscription.verifyPayment({
          razorpayOrderId: orderData.orderId,
          razorpayPaymentId: `free_${Date.now()}`,
          razorpaySignature: 'free_coupon',
        });
        toast.success('Subscription activated with 100% discount!');
        navigate('/payment/success', {
          state: {
            telegramRedirectUrl: verifyRes.data.telegramRedirectUrl,
            linkToken: verifyRes.data.linkToken || verifyRes.data.token,
            subscription: verifyRes.data.subscription,
            user: regRes.data.user,
          },
        });
        return;
      }

      // 3. Open Official Razorpay Checkout Modal
      await openRazorpayCheckout({
        orderId: orderData.orderId,
        amount: orderData.amount,
        currency: orderData.currency || 'INR',
        keyId: orderData.keyId || orderData.key,
        name: 'AlphaTrading Alerts',
        description: 'Telegram Monthly Subscription (₹1,000 / Month)',
        prefill: {
          name: formData.fullName.trim(),
          email: formData.email.trim().toLowerCase(),
          contact: cleanMobile,
        },
        themeColor: '#0088cc',
        onSuccess: async (paymentResponse) => {
          try {
            setLoading(true);
            const verifyRes = await telegramSubscription.verifyPayment({
              razorpayOrderId: paymentResponse.razorpayOrderId,
              razorpayPaymentId: paymentResponse.razorpayPaymentId,
              razorpaySignature: paymentResponse.razorpaySignature,
            });

            toast.success('Payment verified! Redirecting to Telegram...');
            navigate('/payment/success', {
              state: {
                telegramRedirectUrl: verifyRes.data?.telegramRedirectUrl,
                linkToken: verifyRes.data?.linkToken || verifyRes.data?.token,
                subscription: verifyRes.data?.subscription,
                user: regRes.data.user,
              },
            });
          } catch (verifyErr) {
            console.error('Payment verification failed:', verifyErr);
            const errMsg =
              verifyErr.response?.data?.message || 'Payment verification failed. Please contact support.';
            setErrorMsg(errMsg);
            toast.error(errMsg);
          } finally {
            setLoading(false);
          }
        },
        onDismiss: () => {
          setLoading(false);
          toast('Payment window closed. You can retry when ready.', { icon: 'ℹ️' });
        },
        onError: (errorMsg) => {
          setLoading(false);
          setErrorMsg(errorMsg || 'Payment failed. Please try another payment method.');
          toast.error(errorMsg || 'Payment failed');
        },
      });
    } catch (err) {
      console.error('Registration/Payment error:', err);
      const msg = err.response?.data?.message || err.message || 'Something went wrong. Please check your inputs.';
      setErrorMsg(msg);
      toast.error(msg);
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0b0f19', color: '#f8fafc', overflowX: 'hidden' }}>
      <PublicNavbar />

      {/* Hero Section: 2-Column Split Layout */}
      <Box
        sx={{
          position: 'relative',
          pt: { xs: 6, md: 9 },
          pb: { xs: 8, md: 12 },
          background: 'radial-gradient(circle at 20% 15%, rgba(37, 99, 235, 0.15) 0%, rgba(11, 15, 25, 1) 60%), radial-gradient(circle at 80% 30%, rgba(0, 136, 204, 0.12) 0%, rgba(11, 15, 25, 1) 70%)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 5, md: 6, lg: 8 }} alignItems="center">
            {/* Left Column: Value Proposition & Slogan */}
            <Grid item xs={12} md={6.5}>
              <Box>
                {/* Live Pill Badge */}
                <Box sx={{ mb: 2.5 }}>
                  <Chip
                    icon={
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: '#10b981',
                          boxShadow: '0 0 10px #10b981',
                          animation: 'pulseDot 1.8s infinite',
                          '@keyframes pulseDot': {
                            '0%': { opacity: 0.4, transform: 'scale(0.8)' },
                            '50%': { opacity: 1, transform: 'scale(1.2)' },
                            '100%': { opacity: 0.4, transform: 'scale(0.8)' },
                          },
                        }}
                      />
                    }
                    label="LIVE SIGNALS ACTIVE • TELEGRAM VIP BOT"
                    sx={{
                      bgcolor: 'rgba(16, 185, 129, 0.1)',
                      color: '#34d399',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      letterSpacing: '0.06em',
                      border: '1px solid rgba(16, 185, 129, 0.25)',
                      py: 0.6,
                      px: 1,
                    }}
                  />
                </Box>

                {/* Main Slogan & Headline */}
                <Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    fontWeight: 900,
                    fontSize: { xs: '2.4rem', sm: '3.2rem', md: '3.6rem', lg: '4rem' },
                    lineHeight: 1.12,
                    letterSpacing: '-0.03em',
                    mb: 2.5,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 40%, #10b981 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      display: 'inline-block',
                    }}
                  >
                    PAY ₹1,000 AND EARN UNLIMITED
                  </Box>
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    color: '#94a3b8',
                    fontWeight: 400,
                    fontSize: { xs: '1.05rem', md: '1.18rem' },
                    lineHeight: 1.65,
                    mb: 3.5,
                  }}
                >
                  Get institutional-grade stock & options trading alerts delivered with sub-second latency straight into your personal Telegram. Powered by algorithmic momentum and volume breakout models scanning Indian markets all day.
                </Typography>

                {/* Regulatory Risk Disclaimer Callout */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.2,
                    mb: 4,
                    borderRadius: 3,
                    bgcolor: 'rgba(245, 158, 11, 0.08)',
                    border: '1px solid rgba(245, 158, 11, 0.25)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                  }}
                >
                  <WarningAmber sx={{ color: '#fbbf24', fontSize: 26, mt: 0.2, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: '#fef3c7', fontSize: '0.84rem', lineHeight: 1.6 }}>
                    <strong>Risk Disclosure:</strong> Stock and derivatives trading involves substantial risk of loss. Returns and profits are strictly NOT guaranteed. The ₹1,000 payment provides access to our algorithmic trading notification and information service for one month (30 days).
                  </Typography>
                </Paper>

                {/* 4 Key Benefit Bullets */}
                <Stack spacing={1.8} sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: 'rgba(16, 185, 129, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Check sx={{ color: '#10b981', fontSize: 16 }} />
                    </Box>
                    <Typography variant="body1" sx={{ color: '#e2e8f0', fontSize: '0.92rem', fontWeight: 500 }}>
                      <strong>Instant Push Notifications:</strong> Direct alerts to <strong>@JigendraTradingAlerts_bot</strong> on your phone.
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: 'rgba(16, 185, 129, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Check sx={{ color: '#10b981', fontSize: 16 }} />
                    </Box>
                    <Typography variant="body1" sx={{ color: '#e2e8f0', fontSize: '0.92rem', fontWeight: 500 }}>
                      <strong>Full Trade Parameters:</strong> Exact Entry Price, Target 1, Target 2, and Trailing Stop-Loss.
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: 'rgba(16, 185, 129, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Check sx={{ color: '#10b981', fontSize: 16 }} />
                    </Box>
                    <Typography variant="body1" sx={{ color: '#e2e8f0', fontSize: '0.92rem', fontWeight: 500 }}>
                      <strong>Live Updates & Exit Alerts:</strong> Real-time trailing SL adjustments and profit target hit alerts.
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: 'rgba(16, 185, 129, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Check sx={{ color: '#10b981', fontSize: 16 }} />
                    </Box>
                    <Typography variant="body1" sx={{ color: '#e2e8f0', fontSize: '0.92rem', fontWeight: 500 }}>
                      <strong>Flat ₹1,000 / Month:</strong> No commissions, no setup fees, no lock-in contract.
                    </Typography>
                  </Box>
                </Stack>

                {/* Trust Stats Bar */}
                <Grid container spacing={2} sx={{ pt: 1, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                  <Grid item xs={4}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#38bdf8' }}>
                      ⚡ 100ms
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                      Alert Latency
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#34d399' }}>
                      30 Days
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                      Full Access
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#fbbf24' }}>
                      100%
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                      Automated Signals
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Right Column: Premium Registration & Checkout Card */}
            <Grid item xs={12} md={5.5}>
              <Card
                id="register-card"
                elevation={0}
                sx={{
                  bgcolor: '#111827',
                  border: '1px solid rgba(56, 189, 248, 0.3)',
                  borderRadius: 4,
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7), 0 0 45px rgba(37, 99, 235, 0.15)',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                {/* Top Glowing Header Banner */}
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #0284c7 100%)',
                    py: 2.8,
                    px: 3.5,
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="overline" sx={{ color: '#bae6fd', fontWeight: 800, letterSpacing: '0.1em', fontSize: '0.72rem' }}>
                        MEMBER REGISTRATION
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#ffffff', lineHeight: 1.2 }}>
                        VIP Telegram Access
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: 'right' }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Typography variant="h4" sx={{ fontWeight: 900, color: '#ffffff' }}>
                          ₹1,000
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#bfdbfe', fontWeight: 600 }}>
                          / mo
                        </Typography>
                      </Box>
                      <Chip
                        label="Instant Activation"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                          color: '#fff',
                        }}
                      />
                    </Box>
                  </Stack>
                </Box>

                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                  {errorMsg && (
                    <Alert
                      severity="error"
                      onClose={() => setErrorMsg('')}
                      sx={{
                        mb: 3,
                        borderRadius: 2.5,
                        bgcolor: 'rgba(239, 68, 68, 0.12)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        color: '#fca5a5',
                        '& .MuiAlert-icon': { color: '#f87171' },
                      }}
                    >
                      {errorMsg}
                    </Alert>
                  )}

                  <Box component="form" onSubmit={handleRegisterAndPay}>
                    <Stack spacing={2.4}>
                      {/* Full Name */}
                      <TextField
                        fullWidth
                        label="Full Name"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        placeholder="e.g. Rahul Sharma"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                          sx: { color: '#94a3b8', fontWeight: 600, fontSize: '0.9rem', '&.Mui-focused': { color: '#38bdf8' } },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonOutline sx={{ color: '#38bdf8', fontSize: 22 }} />
                            </InputAdornment>
                          ),
                          sx: {
                            bgcolor: 'rgba(15, 23, 42, 0.7)',
                            borderRadius: 2.5,
                            color: '#ffffff',
                            fontSize: '0.95rem',
                            fontWeight: 500,
                            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.12)', borderRadius: 2.5 },
                            '&:hover fieldset': { borderColor: 'rgba(56, 189, 248, 0.4)' },
                            '&.Mui-focused fieldset': { borderColor: '#38bdf8', borderWidth: 2 },
                            '& input': { py: 1.5, '&::placeholder': { color: '#64748b', opacity: 1 } },
                          },
                        }}
                      />

                      {/* Mobile Number with +91 indicator */}
                      <TextField
                        fullWidth
                        label="Mobile Number (10 Digits)"
                        name="mobileNumber"
                        value={formData.mobileNumber}
                        onChange={handleChange}
                        required
                        placeholder="9876543210"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                          sx: { color: '#94a3b8', fontWeight: 600, fontSize: '0.9rem', '&.Mui-focused': { color: '#38bdf8' } },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Stack direction="row" alignItems="center" spacing={0.8} sx={{ pr: 1, borderRight: '1px solid rgba(255,255,255,0.12)', mr: 0.5 }}>
                                <PhoneAndroid sx={{ color: '#38bdf8', fontSize: 20 }} />
                                <Typography variant="body2" sx={{ color: '#e2e8f0', fontWeight: 700, fontSize: '0.9rem' }}>
                                  +91
                                </Typography>
                              </Stack>
                            </InputAdornment>
                          ),
                          sx: {
                            bgcolor: 'rgba(15, 23, 42, 0.7)',
                            borderRadius: 2.5,
                            color: '#ffffff',
                            fontSize: '0.95rem',
                            fontWeight: 500,
                            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.12)', borderRadius: 2.5 },
                            '&:hover fieldset': { borderColor: 'rgba(56, 189, 248, 0.4)' },
                            '&.Mui-focused fieldset': { borderColor: '#38bdf8', borderWidth: 2 },
                            '& input': { py: 1.5, letterSpacing: '0.04em', '&::placeholder': { color: '#64748b', opacity: 1 } },
                          },
                        }}
                      />

                      {/* Email Address */}
                      <TextField
                        fullWidth
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="rahul@gmail.com"
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                          sx: { color: '#94a3b8', fontWeight: 600, fontSize: '0.9rem', '&.Mui-focused': { color: '#38bdf8' } },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <MailOutline sx={{ color: '#38bdf8', fontSize: 22 }} />
                            </InputAdornment>
                          ),
                          sx: {
                            bgcolor: 'rgba(15, 23, 42, 0.7)',
                            borderRadius: 2.5,
                            color: '#ffffff',
                            fontSize: '0.95rem',
                            fontWeight: 500,
                            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.12)', borderRadius: 2.5 },
                            '&:hover fieldset': { borderColor: 'rgba(56, 189, 248, 0.4)' },
                            '&.Mui-focused fieldset': { borderColor: '#38bdf8', borderWidth: 2 },
                            '& input': { py: 1.5, '&::placeholder': { color: '#64748b', opacity: 1 } },
                          },
                        }}
                      />

                      {/* Password & Confirm Password Grid */}
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Password"
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            value={formData.password}
                            onChange={handleChange}
                            required
                            placeholder="Min 6 chars"
                            variant="outlined"
                            InputLabelProps={{
                              shrink: true,
                              sx: { color: '#94a3b8', fontWeight: 600, fontSize: '0.85rem', '&.Mui-focused': { color: '#38bdf8' } },
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Lock sx={{ color: '#38bdf8', fontSize: 18 }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => setShowPassword(!showPassword)}
                                    edge="end"
                                    size="small"
                                    sx={{ color: '#94a3b8', '&:hover': { color: '#ffffff' } }}
                                  >
                                    {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                              sx: {
                                bgcolor: 'rgba(15, 23, 42, 0.7)',
                                borderRadius: 2.5,
                                color: '#ffffff',
                                fontSize: '0.9rem',
                                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.12)', borderRadius: 2.5 },
                                '&:hover fieldset': { borderColor: 'rgba(56, 189, 248, 0.4)' },
                                '&.Mui-focused fieldset': { borderColor: '#38bdf8', borderWidth: 2 },
                                '& input': { py: 1.5, '&::placeholder': { color: '#64748b', opacity: 1 } },
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Confirm Password"
                            name="confirmPassword"
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            required
                            placeholder="Repeat"
                            variant="outlined"
                            InputLabelProps={{
                              shrink: true,
                              sx: { color: '#94a3b8', fontWeight: 600, fontSize: '0.85rem', '&.Mui-focused': { color: '#38bdf8' } },
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Lock sx={{ color: '#38bdf8', fontSize: 18 }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    edge="end"
                                    size="small"
                                    sx={{ color: '#94a3b8', '&:hover': { color: '#ffffff' } }}
                                  >
                                    {showConfirmPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                              sx: {
                                bgcolor: 'rgba(15, 23, 42, 0.7)',
                                borderRadius: 2.5,
                                color: '#ffffff',
                                fontSize: '0.9rem',
                                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.12)', borderRadius: 2.5 },
                                '&:hover fieldset': { borderColor: 'rgba(56, 189, 248, 0.4)' },
                                '&.Mui-focused fieldset': { borderColor: '#38bdf8', borderWidth: 2 },
                                '& input': { py: 1.5, '&::placeholder': { color: '#64748b', opacity: 1 } },
                              },
                            }}
                          />
                        </Grid>
                      </Grid>

                      {/* Mandatory Terms & Conditions Checkbox Card */}
                      <Box
                        sx={{
                          p: 1.8,
                          borderRadius: 2.5,
                          bgcolor: formData.termsAccepted ? 'rgba(37, 99, 235, 0.08)' : 'rgba(15, 23, 42, 0.6)',
                          border: formData.termsAccepted ? '1px solid rgba(56, 189, 248, 0.4)' : '1px solid rgba(255, 255, 255, 0.1)',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="termsAccepted"
                              checked={formData.termsAccepted}
                              onChange={handleChange}
                              sx={{
                                color: '#64748b',
                                '&.Mui-checked': { color: '#38bdf8' },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ color: '#cbd5e1', fontSize: '0.82rem', lineHeight: 1.5 }}>
                              I agree to the{' '}
                              <RouterLink to="/terms" target="_blank" style={{ color: '#38bdf8', fontWeight: 600, textDecoration: 'underline' }}>
                                Terms & Conditions
                              </RouterLink>{' '}
                              and{' '}
                              <RouterLink to="/privacy" target="_blank" style={{ color: '#38bdf8', fontWeight: 600, textDecoration: 'underline' }}>
                                Privacy Policy
                              </RouterLink>
                              , and acknowledge that market trading involves financial risk.
                            </Typography>
                          }
                          sx={{ m: 0, alignItems: 'flex-start', '& .MuiCheckbox-root': { p: 0.5, mr: 1 } }}
                        />
                      </Box>

                      {/* Coupon Code Section */}
                      <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2.5, border: '1px dashed rgba(255,255,255,0.15)' }}>
                        {appliedCoupon ? (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Discount sx={{ color: '#10b981', fontSize: 20 }} />
                              <Box>
                                <Typography variant="caption" sx={{ color: '#34d399', fontWeight: 700, display: 'block' }}>
                                  Coupon "{appliedCoupon.code}" Applied!
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                  Saved ₹{appliedCoupon.discountAmount}. Final payable: ₹{appliedCoupon.finalPrice}
                                </Typography>
                              </Box>
                            </Box>
                            <Button size="small" color="error" onClick={handleRemoveCoupon} sx={{ textTransform: 'none', fontWeight: 600 }}>
                              Remove
                            </Button>
                          </Box>
                        ) : (
                          <Stack direction="row" spacing={1}>
                            <TextField
                              size="small"
                              placeholder="Have a coupon? (e.g. WELCOME20)"
                              value={couponInput}
                              onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                              sx={{
                                flex: 1,
                                '& .MuiOutlinedInput-root': {
                                  color: '#fff',
                                  fontSize: '0.85rem',
                                  bgcolor: 'rgba(0,0,0,0.2)',
                                  '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                                  '&:hover fieldset': { borderColor: '#38bdf8' },
                                },
                              }}
                            />
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={handleApplyCoupon}
                              disabled={couponLoading || !couponInput.trim()}
                              sx={{
                                borderColor: 'rgba(56,189,248,0.4)',
                                color: '#38bdf8',
                                fontWeight: 700,
                                textTransform: 'none',
                                px: 2,
                              }}
                            >
                              {couponLoading ? <CircularProgress size={16} color="inherit" /> : 'Apply'}
                            </Button>
                          </Stack>
                        )}
                      </Box>

                      {/* Prominent CTA Submit Button */}
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={loading}
                        endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ArrowForward />}
                        startIcon={<Telegram sx={{ fontSize: '24px !important' }} />}
                        sx={{
                          py: 1.8,
                          fontSize: '1rem',
                          fontWeight: 800,
                          letterSpacing: '0.02em',
                          borderRadius: 3,
                          background: 'linear-gradient(135deg, #0088cc 0%, #2563eb 50%, #1d4ed8 100%)',
                          boxShadow: '0 8px 24px rgba(0, 136, 204, 0.35)',
                          color: '#ffffff',
                          textTransform: 'none',
                          transition: 'all 0.25s ease',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #0099e6 0%, #3b82f6 50%, #2563eb 100%)',
                            boxShadow: '0 12px 32px rgba(0, 136, 204, 0.55)',
                            transform: 'translateY(-1px)',
                          },
                          '&:disabled': {
                            background: 'rgba(255, 255, 255, 0.1)',
                            color: 'rgba(255, 255, 255, 0.35)',
                          },
                        }}
                      >
                        {loading
                          ? 'Processing Enrollment...'
                          : `PAY ₹${appliedCoupon ? appliedCoupon.finalPrice : '1,000'} & START TELEGRAM BOT`}
                      </Button>

                      {/* Payment Trust Footer */}
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} sx={{ pt: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, color: '#64748b', fontSize: '0.74rem' }}>
                          <Security sx={{ fontSize: 15, color: '#10b981' }} />
                          256-Bit SSL
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, color: '#64748b', fontSize: '0.74rem' }}>
                          <Shield sx={{ fontSize: 15, color: '#38bdf8' }} />
                          Razorpay Secured
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, color: '#64748b', fontSize: '0.74rem' }}>
                          <CheckCircle sx={{ fontSize: 15, color: '#fbbf24' }} />
                          UPI • Cards • NetBanking
                        </Box>
                      </Stack>
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Realistic Live Telegram Signal Mockup Widget */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#070b12', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: 700, mx: 'auto', mb: 6 }}>
            <Chip
              label="SAMPLE TELEGRAM FEED"
              size="small"
              sx={{ bgcolor: 'rgba(56, 189, 248, 0.12)', color: '#38bdf8', fontWeight: 700, mb: 1.5 }}
            />
            <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, mb: 1.5 }}>
              What You Receive in Telegram
            </Typography>
            <Typography variant="body1" sx={{ color: '#94a3b8' }}>
              Direct, crystal-clear alerts with zero clutter. Get actionable price levels, stop losses, and target targets in real time.
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {/* Mock Telegram Bubble 1: BUY ALERT */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  bgcolor: '#17212b',
                  borderRadius: 3.5,
                  p: 3,
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
                  fontFamily: 'monospace',
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, pb: 1.5, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#0088cc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Telegram sx={{ color: '#fff', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, fontFamily: 'inherit' }}>
                      Jigendra Trading Alerts Bot <Chip label="OFFICIAL BOT" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#2b5278', color: '#fff' }} />
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6c7883' }}>
                      @JigendraTradingAlerts_bot
                    </Typography>
                  </Box>
                </Stack>

                <Typography variant="body2" sx={{ color: '#34d399', fontWeight: 700, mb: 1, fontFamily: 'inherit', fontSize: '0.95rem' }}>
                  🚀 NEW TRADE ALERT: RELIANCE BUY
                </Typography>
                <Typography variant="body2" sx={{ color: '#e2e8f0', lineHeight: 1.8, fontFamily: 'inherit', fontSize: '0.88rem' }}>
                  <strong>Instrument:</strong> RELIANCE (Reliance Industries)<br />
                  <strong>Action:</strong> BUY RECOMMENDATION<br />
                  <strong>Entry Price:</strong> ₹1,292.50 (Range: ₹1,288 – ₹1,298)<br />
                  <strong>Stop Loss:</strong> ₹1,240.00<br />
                  <strong>Target 1:</strong> ₹1,350.00<br />
                  <strong>Target 2:</strong> ₹1,390.00<br />
                  <strong>Risk/Reward:</strong> 1 : 2.5 | <strong>AI Confidence:</strong> 88%<br />
                  <strong>Strategy:</strong> Momentum Breakout + VWAP Confirmation
                </Typography>
                <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.08)' }} />
                <Typography variant="caption" sx={{ color: '#79828b', display: 'block', textAlign: 'right', fontFamily: 'inherit' }}>
                  09:48 AM • Delivered to VIP Subscribers
                </Typography>
              </Paper>
            </Grid>

            {/* Mock Telegram Bubble 2: PROFIT / EXIT ALERT */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  bgcolor: '#17212b',
                  borderRadius: 3.5,
                  p: 3,
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
                  fontFamily: 'monospace',
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2, pb: 1.5, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#0088cc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Telegram sx={{ color: '#fff', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, fontFamily: 'inherit' }}>
                      Jigendra Trading Alerts Bot <Chip label="OFFICIAL BOT" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#2b5278', color: '#fff' }} />
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6c7883' }}>
                      @JigendraTradingAlerts_bot
                    </Typography>
                  </Box>
                </Stack>

                <Typography variant="body2" sx={{ color: '#38bdf8', fontWeight: 700, mb: 1, fontFamily: 'inherit', fontSize: '0.95rem' }}>
                  🎯 TARGET HIT: BANKNIFTY 48200 CE
                </Typography>
                <Typography variant="body2" sx={{ color: '#e2e8f0', lineHeight: 1.8, fontFamily: 'inherit', fontSize: '0.88rem' }}>
                  <strong>Instrument:</strong> BANKNIFTY 48200 CE<br />
                  <strong>Entry Premium:</strong> ₹285.50<br />
                  <strong>Exit Price:</strong> ₹345.00<br />
                  <strong>Realized Return:</strong> <strong>+20.8% (+₹59.50/share)</strong><br />
                  <strong>Status:</strong> Target 1 Achieved • Trailing SL Updated<br />
                  <strong>Next Trailing SL:</strong> ₹320.00 (Risk-Free Breakeven)
                </Typography>
                <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.08)' }} />
                <Typography variant="caption" sx={{ color: '#79828b', display: 'block', textAlign: 'right', fontFamily: 'inherit' }}>
                  11:15 AM • Delivered to VIP Subscribers
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 3 Step Simple Flow */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#0b0f19', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip label="QUICK ONBOARDING" size="small" sx={{ bgcolor: 'rgba(251, 191, 36, 0.12)', color: '#fbbf24', fontWeight: 700, mb: 1.5 }} />
            <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, mb: 1.5 }}>
              How to Get Started in 3 Steps
            </Typography>
            <Typography variant="body1" sx={{ color: '#94a3b8' }}>
              No complex software to install. You receive real-time notifications on the Telegram app you already use.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  bgcolor: 'rgba(17, 24, 39, 0.8)',
                  p: 4,
                  borderRadius: 3.5,
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: '#3b82f6', transform: 'translateY(-3px)' },
                }}
              >
                <Box sx={{ width: 48, height: 48, borderRadius: 2.5, bgcolor: 'rgba(37, 99, 235, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5 }}>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: '#60a5fa' }}>1</Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.2, color: '#fff' }}>
                  Register Account
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.7 }}>
                  Fill out your name, 10-digit mobile number, email, and password. Review and accept our market risk terms.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  bgcolor: 'rgba(17, 24, 39, 0.8)',
                  p: 4,
                  borderRadius: 3.5,
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: '#10b981', transform: 'translateY(-3px)' },
                }}
              >
                <Box sx={{ width: 48, height: 48, borderRadius: 2.5, bgcolor: 'rgba(16, 185, 129, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5 }}>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: '#34d399' }}>2</Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.2, color: '#fff' }}>
                  Pay ₹1,000 / Month
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.7 }}>
                  Complete the secure ₹1,000 monthly fee via Razorpay using UPI (GPay, PhonePe, Paytm), NetBanking, or Debit/Credit card.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  bgcolor: 'rgba(17, 24, 39, 0.8)',
                  p: 4,
                  borderRadius: 3.5,
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: '#0088cc', transform: 'translateY(-3px)' },
                }}
              >
                <Box sx={{ width: 48, height: 48, borderRadius: 2.5, bgcolor: 'rgba(0, 136, 204, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5 }}>
                  <Telegram sx={{ color: '#0088cc', fontSize: 28 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.2, color: '#fff' }}>
                  Tap START in Telegram
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.7 }}>
                  Click the <strong>[ START TELEGRAM ]</strong> button on the success screen. The bot verifies your subscription and starts sending instant alerts!
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#070b12' }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip label="HAVE QUESTIONS?" size="small" sx={{ bgcolor: 'rgba(255, 255, 255, 0.08)', color: '#cbd5e1', fontWeight: 700, mb: 1.5 }} />
            <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, mb: 1.5 }}>
              Frequently Asked Questions
            </Typography>
            <Typography variant="body1" sx={{ color: '#94a3b8' }}>
              Clear, transparent answers before you subscribe.
            </Typography>
          </Box>

          <Stack spacing={2}>
            <Accordion
              elevation={0}
              sx={{
                bgcolor: '#111827',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '16px !important',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#38bdf8' }} />}>
                <Typography variant="subtitle1" fontWeight={700}>
                  How quickly do I get access after paying ₹1,000?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ color: '#94a3b8', pt: 0, lineHeight: 1.7 }}>
                Instantly! The moment your payment is confirmed by Razorpay, you will be redirected to the Payment Success screen featuring a prominent <strong>[ START TELEGRAM ]</strong> button. Clicking it links your Telegram account automatically in seconds.
              </AccordionDetails>
            </Accordion>

            <Accordion
              elevation={0}
              sx={{
                bgcolor: '#111827',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '16px !important',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#38bdf8' }} />}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Are trading returns or profits guaranteed?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ color: '#94a3b8', pt: 0, lineHeight: 1.7 }}>
                No. Securities and derivatives trading carries substantial market risk of capital loss. While our models are designed to identify high-probability setups, market movements are inherently uncertain. The ₹1,000 monthly fee is exclusively for access to our software notification service.
              </AccordionDetails>
            </Accordion>

            <Accordion
              elevation={0}
              sx={{
                bgcolor: '#111827',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '16px !important',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#38bdf8' }} />}>
                <Typography variant="subtitle1" fontWeight={700}>
                  What payment methods are supported?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ color: '#94a3b8', pt: 0, lineHeight: 1.7 }}>
                We support all major payment methods via Razorpay: UPI (Google Pay, PhonePe, Paytm, BHIM), Debit Cards, Credit Cards, and NetBanking across all Indian banks.
              </AccordionDetails>
            </Accordion>

            <Accordion
              elevation={0}
              sx={{
                bgcolor: '#111827',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '16px !important',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#38bdf8' }} />}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Can I cancel my subscription anytime?
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ color: '#94a3b8', pt: 0, lineHeight: 1.7 }}>
                Yes. There is no auto-lock or mandatory long-term contract. Each payment activates 30 calendar days of Telegram alerts. If you decide not to continue, you are under no obligation to renew.
              </AccordionDetails>
            </Accordion>
          </Stack>
        </Container>
      </Box>

      {/* Footer with Full Regulatory Disclaimers */}
      <PublicFooter />

    </Box>
  );
}
